import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import type { ContentMasterWawiLanguageView } from '../../api/generated/models/ContentMasterWawiLanguageView';
import {
  isWawiLanguageCodeAllowed,
  pickDefaultWawiLanguageCode,
  wawiLanguageLabel,
  wawiLanguageToApiValue,
} from '../../utils/contentMasterWawiLanguage';
import {
  type MarketplaceCode,
  isMarketplaceSource,
  isMarketplaceSyncFresh,
} from '../../utils/marketplaceListing';
import type { ContentMasterWorkspacePlatformView } from '../../api/generated/models/ContentMasterWorkspacePlatformView';
import { ConfigView } from '../../api/generated/models/ConfigView';
import type { ContentMasterWorkspaceDefaultLanguageView } from '../../api/generated/models/ContentMasterWorkspaceDefaultLanguageView';
import type { ContentMasterProductView } from '../../api/generated/models/ContentMasterProductView';
import { MarketplaceProductFieldsView } from '../../api/generated/models/MarketplaceProductFieldsView';
import MarketplaceCategoryPicker from './MarketplaceCategoryPicker';
import MarketplaceSyncPopconfirm from './MarketplaceSyncPopconfirm';
import AdditionalContextInput, {
  type AdditionalContextState,
  buildAdditionalContext,
  emptyAdditionalContextState,
  hasParsingAdditionalContextFiles,
  ADDITIONAL_CONTEXT_MAX_LENGTH,
} from './AdditionalContextInput';
import { ContentMasterProductThumb } from './ContentMasterProductThumb';
import AutoDetectCategoryWarningModal, {
  isAutoDetectWarningDismissed,
} from './AutoDetectCategoryWarningModal';
import EmptyFieldPolicyControl from './EmptyFieldPolicyControl';
import type { ContentMasterEmptyFieldPolicy } from '../../store/slices/contentMasterSlice';
import InfoPopover from '../ui/InfoPopover';
import happyBot from '../../assets/img/happy-bot.png';
import {
  estimateAdditionalContextTokens,
  estimateContentMasterAiTokens,
} from '../../utils/contentMasterTokenEstimate';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  clearMarketplaceSyncStatus,
  fetchMarketplaceSyncStatus,
  refreshMarketplaceMetadata,
  type MarketplaceRefreshType,
} from '../../store/slices/marketplaceListingSlice';
import { addNotification } from '../../store/slices/notificationSlice';

/** SCX attribute types the AI never generates - excluded from the context
 * token surcharge field count (mirrors the backend chunking rule). */
const NON_GENERATED_ATTR_TYPES = new Set(['image', 'document', 'url', 'label']);

/** Locale date (date only) for a sync timestamp; em dash when missing/invalid. */
const formatSyncDate = (iso?: string | null): string => {
  if (!iso) {
    return '-';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/** Payload handed back to the page when the user confirms the modal. */
export interface BulkGenerateConfirmParams {
  source: string;
  language: string;
  /**
   * `true` → route the batch through the marketplace-listing flow
   * (`POST /v2/api/marketplace-listing/generate`). `false` → existing Content
   * Master bulk generate. Only ever `true` for Marktplatz channels.
   */
  prepareListing: boolean;
  /** Marketplace override; `null` lets the backend auto-detect from the channel. */
  marketplace: MarketplaceCode | null;
  /** Field-group toggles for listing generation (which attributes to generate). */
  generateRequired: boolean;
  generateRecommended: boolean;
  generateOther: boolean;
  /**
   * Also generate attributes Wawi already pre-fills for the channel/category
   * (`generatePrefilled` of POST /generate and /generate-sync). Default false -
   * the backend skips (and does not bill) pre-filled attributes.
   */
  generatePrefilled: boolean;
  /**
   * "Fill only empty fields" (`onlyEmptyFields` of POST /generate and
   * /generate-sync): attributes that already have a value in Wawi for this
   * channel and an existing channel description are left untouched. Default
   * false. Listing flow only.
   */
  onlyEmptyFields: boolean;
  /**
   * Listing-flow generation items (only set when `prepareListing` is `true`).
   * Products without a marketplace category are excluded unless `autoDetectCategory`
   * is on (then the backend resolves the category for them).
   */
  listingItems?: Array<{ wawiArtikelId: number }>;
  /** Request-level "auto-detect category" flag for the listing generate call. */
  autoDetectCategory: boolean;
  /**
   * `additionalContext` for `POST /marketplace-listing/generate-sync` - only
   * set by the `'single'` variant (bulk does not support it) and only when
   * non-empty.
   */
  additionalContext?: string;
  /**
   * Opt-in for the URL path (`urlPath` / `cUrlPfad`) of the classic Content
   * Master bulk generate (`POST /bulk/generate.includeUrlPath`). Default false
   * - regenerating replaces shop URLs that are already live. Always `false`
   * for the listing flow (the flag does not apply there).
   */
  includeUrlPath: boolean;
  /**
   * Explicit user choice for fields the AI returns EMPTY - `emptyFieldPolicy`
   * of the classic Content Master bulk generate (`POST /bulk/generate`).
   * Always `keepOriginal` for the listing flow (the parameter does not apply
   * there).
   */
  emptyFieldPolicy: ContentMasterEmptyFieldPolicy;
}

export interface BulkGenerateConfirmModalProps {
  /**
   * `'bulk'` (default) - launched from the product list for N products, routed
   * through the queued `POST /marketplace-listing/generate`. `'single'` - the
   * single-product page for one Marktplatz product; the caller wires
   * `onConfirm` to the synchronous `POST /marketplace-listing/generate-sync`.
   * In `'single'` the listing UI is always shown (we know it is a marketplace
   * product) regardless of the resolved platform metadata.
   */
  variant?: 'bulk' | 'single';
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  platforms: ContentMasterWorkspacePlatformView[] | undefined;
  languages: ContentMasterWawiLanguageView[];
  defaultWorkspaceLanguage?: ContentMasterWorkspaceDefaultLanguageView | null;
  initialSource: string;
  initialLanguage: string;
  preferDeUi: boolean;
  isSubmitting: boolean;
  /** Wawi data source - forwarded to the sync-status request. */
  dataSourceId?: string | null;
  /**
   * The selected products (resolved from the page list by `wawiArtikelId`). Used
   * by the listing flow to detect products without a marketplace category
   * (`marketplace.status === category_not_selected`) so the user can assign one
   * or opt into auto-detection before generating.
   */
  selectedProducts: ContentMasterProductView[];
  /**
   * Public global configs from `GET /content-master/workspace-config.publicConfigs`
   * (token cost model). Used for the listing-flow token estimate; the estimate
   * row is hidden when the required keys are missing.
   */
  publicConfigs?: ConfigView[] | null;
  onConfirm: (params: BulkGenerateConfirmParams) => void;
}

/**
 * Confirmation modal shown right before a bulk batch is launched. Lets the
 * user pick the channel and language and shows the projected token cost.
 *
 * When the selected channel is a Marktplatz (`nTypLabel === "Marktplatz"`),
 * the modal additionally offers a "Prepare for marketplace listing" toggle.
 * While it is on, the modal pulls `GET /marketplace-listing/sync-status` and
 * shows a freshness indicator (non-blocking) plus an optional marketplace
 * override; confirming then routes the batch into the listing flow. With the
 * toggle off (or on non-marketplace channels) the behaviour is unchanged -
 * the classic Content Master bulk generate.
 */
const BulkGenerateConfirmModal: React.FC<BulkGenerateConfirmModalProps> = ({
  variant = 'bulk',
  isOpen,
  onClose,
  selectedCount,
  platforms,
  languages,
  defaultWorkspaceLanguage,
  initialSource,
  initialLanguage,
  preferDeUi,
  isSubmitting,
  dataSourceId,
  selectedProducts,
  publicConfigs,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { syncStatus, syncStatusLoading, refreshSubmitting } = useAppSelector(
    (state) => state.marketplaceListing
  );

  const [source, setSource] = useState<string>(initialSource);
  const [language, setLanguage] = useState<string>(() => {
    if (initialLanguage && isWawiLanguageCodeAllowed(initialLanguage, languages)) {
      return initialLanguage;
    }
    return pickDefaultWawiLanguageCode(languages, defaultWorkspaceLanguage);
  });

  const sourceIsMarketplace = useMemo(
    () => isMarketplaceSource(platforms, source),
    [platforms, source]
  );

  // The modal's mode: `single` is always a Marktplatz product (listing UI); in
  // `bulk` it follows the channel selected in the product search - marketplace
  // channel → listing UI, otherwise → Content Master (language only).
  const listingActive = variant === 'single' || sourceIsMarketplace;

  /** Field-group toggles ("which attributes to generate"). Backend defaults: required=on. */
  const [generateRequired, setGenerateRequired] = useState(true);
  const [generateRecommended, setGenerateRecommended] = useState(true);
  const [generateOther, setGenerateOther] = useState(false);
  /**
   * Also generate attributes that Wawi already pre-fills for the channel /
   * category ("Verkaufskanalfelder zuordnen", incl. dynamic references). Off
   * by default - the backend skips them and does not bill for them; on apply
   * a generated value overrides the Wawi default for the article.
   */
  const [generatePrefilled, setGeneratePrefilled] = useState(false);
  /** "Fill only empty fields" - independent of `generatePrefilled` (new backend flag). */
  const [onlyEmptyFields, setOnlyEmptyFields] = useState(false);
  /** Categories assigned in-modal (categoryName by `wawiArtikelId`) - resolves the product. */
  const [assignedCategory, setAssignedCategory] = useState<Record<number, string>>({});
  /**
   * Single "auto-detect category" opt-in for the whole batch. The generate
   * endpoint takes ONE request-level `autoDetectCategory` flag (not per product):
   * when on, the backend AI-detects + assigns a category for every product in
   * the request that has none.
   */
  const [autoDetectCategory, setAutoDetectCategory] = useState(false);
  /**
   * Consent step for auto-detect: checking the box first opens a warning that
   * detected categories are assigned and written directly to Wawi (see the
   * generate `autoDetectCategory` flag in OpenAPI); the flag is only set on
   * confirm.
   */
  const [autoDetectWarningOpen, setAutoDetectWarningOpen] = useState(false);
  /** Which product's inline category picker is open (`wawiArtikelId` or `null`). */
  const [pickerOpenFor, setPickerOpenFor] = useState<number | null>(null);
  /**
   * Inline "data is fresh - sync anyway?" popconfirm state: which refresh
   * button it is anchored to (`'all'` or a sync-row key) and the types the
   * confirmed refresh should queue (`undefined` = all types).
   */
  const [refreshConfirm, setRefreshConfirm] = useState<{
    anchor: string;
    types?: MarketplaceRefreshType[];
  } | null>(null);
  /**
   * "Additional product information" of the `'single'` variant (bulk does not
   * support `additionalContext`). Deliberately NOT reset by the reopen effect:
   * the context lives one REQUEST, not one modal open - the user may re-open
   * and re-generate with the same context (it is never stored on the server).
   */
  const [additionalContext, setAdditionalContext] = useState<AdditionalContextState>(
    emptyAdditionalContextState
  );
  /**
   * "Also generate URL paths" opt-in of the classic CM bulk flow
   * (`includeUrlPath`). Off by default: regenerating `urlPath` replaces shop
   * URLs that are already live/indexed.
   */
  const [includeUrlPath, setIncludeUrlPath] = useState(false);
  /**
   * "What to do with empty fields?" of the classic CM bulk flow
   * (`emptyFieldPolicy`). Backend default `keepOriginal`: an empty AI value is
   * skipped and the current Wawi value stays.
   */
  const [emptyFieldPolicy, setEmptyFieldPolicy] =
    useState<ContentMasterEmptyFieldPolicy>('keepOriginal');

  // Re-seed local state every time the modal is reopened so a closed-then-
  // reopened modal always reflects the current page filters, not a stale
  // selection from a previous open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSource(initialSource);
    if (initialLanguage && isWawiLanguageCodeAllowed(initialLanguage, languages)) {
      setLanguage(initialLanguage);
    } else {
      setLanguage(pickDefaultWawiLanguageCode(languages, defaultWorkspaceLanguage));
    }
    setGenerateRequired(true);
    setGenerateRecommended(true);
    setGenerateOther(false);
    setGeneratePrefilled(false);
    setOnlyEmptyFields(false);
    setIncludeUrlPath(false);
    setEmptyFieldPolicy('keepOriginal');
    setAssignedCategory({});
    setAutoDetectCategory(false);
    setAutoDetectWarningOpen(false);
    setPickerOpenFor(null);
    setRefreshConfirm(null);
  }, [isOpen, initialSource, initialLanguage, languages, defaultWorkspaceLanguage, platforms]);

  // Pull the freshness indicator whenever the channel is a marketplace. Cleared
  // for non-marketplace channels so a stale indicator never lingers.
  useEffect(() => {
    if (!isOpen || !listingActive || !source) {
      dispatch(clearMarketplaceSyncStatus());
      return;
    }
    dispatch(fetchMarketplaceSyncStatus({ source, dataSourceId }));
  }, [isOpen, listingActive, source, dataSourceId, dispatch]);

  /**
   * Content Master bulk token estimate: base rate per product from the public
   * billing configs (`fieldsCount = 0` - the worker resolves the field list
   * from Wawi at run time, so the client-side estimate is the base-rate lower
   * bound; the server pre-flight prices the exact field count itself).
   */
  const tokenEstimate = useMemo<number | null>(
    () => estimateContentMasterAiTokens(publicConfigs, 0, selectedCount, 'generate'),
    [publicConfigs, selectedCount]
  );

  /**
   * Rough listing-generation duration estimate (backend heuristics): 2s per
   * product + 3s per started block of 20 attributes (`attributeCounts`,
   * selected groups only) + AI category detection per product that needs it -
   * the SUM of the checked field groups: required 10s, recommended 20s,
   * other 60s. Products skipped on submit (no category, auto-detect off) are
   * excluded.
   */
  const listingSecondsEstimate = useMemo<number>(() => {
    const autoDetectSeconds =
      (generateRequired ? 7 : 0) +
      (generateRecommended ? 15 : 0) +
      (generateOther ? 25 : 0);
    let seconds = 0;
    selectedProducts.forEach((p) => {
      const id = p.wawiArtikelId;
      if (typeof id !== 'number') {
        return;
      }
      const needsCategory =
        p.marketplace?.status ===
          MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED &&
        assignedCategory[id] === undefined;
      if (needsCategory && !autoDetectCategory) {
        return; // skipped on submit
      }
      seconds += 2;
      const counts = p.marketplace?.attributeCounts;
      const attrCount =
        (generateRequired ? counts?.required ?? 0 : 0) +
        (generateRecommended ? counts?.recommended ?? 0 : 0) +
        (generateOther ? counts?.other ?? 0 : 0);
      seconds += Math.ceil(attrCount / 20) * 3;
      if (needsCategory && autoDetectCategory) {
        seconds += autoDetectSeconds;
      }
    });
    return seconds;
  }, [
    selectedProducts,
    assignedCategory,
    autoDetectCategory,
    generateRequired,
    generateRecommended,
    generateOther,
  ]);

  /**
   * Listing-generation token estimate from workspace-config `publicConfigs`:
   * per product `base_tokens_generate + attrCount × tokens_per_field_generate`
   * (`attributeCounts`, selected groups only), plus `base_tokens_category_detect`
   * per product that needs AI category detection. The sum is scaled by the
   * tier-1 model multiplier (the modal has no model choice). Products skipped
   * on submit are excluded - same rule as `listingSecondsEstimate`. `null` →
   * required config keys missing, the row is hidden.
   */
  const listingTokensEstimate = useMemo<number | null>(() => {
    const cfg = (name: ConfigView.name): number | null => {
      const raw = publicConfigs?.find((c) => c.name === name)?.value;
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) ? n : null;
    };
    const baseGenerate = cfg(ConfigView.name.CONTENT_MASTER_BASE_TOKENS_GENERATE);
    const perField = cfg(ConfigView.name.CONTENT_MASTER_TOKENS_PER_FIELD_GENERATE);
    const baseCategoryDetect = cfg(
      ConfigView.name.CONTENT_MASTER_BASE_TOKENS_CATEGORY_DETECT
    );
    const tier1Multiplier =
      cfg(ConfigView.name.CONTENT_MASTER_MODEL_TIER_1_MULTIPLIER) ?? 1;
    if (baseGenerate == null || perField == null) {
      return null;
    }
    let tokens = 0;
    selectedProducts.forEach((p) => {
      const id = p.wawiArtikelId;
      if (typeof id !== 'number') {
        return;
      }
      const needsCategory =
        p.marketplace?.status ===
          MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED &&
        assignedCategory[id] === undefined;
      if (needsCategory && !autoDetectCategory) {
        return; // skipped on submit
      }
      const attrs = p.marketplace?.attributes;
      let attrCount: number;
      if (attrs && attrs.length > 0) {
        // Full schema loaded (single-product modal): exact count - selected
        // groups (disjoint, like attributeCounts), and with `generatePrefilled`
        // OFF the backend skips (and does not bill) attributes that Wawi
        // already pre-fills (`defaultValue != null`, incl. placeholders).
        attrCount = attrs.filter((attr) => {
          const included = attr.required
            ? generateRequired
            : attr.recommended
            ? generateRecommended
            : generateOther;
          return included && (generatePrefilled || attr.defaultValue == null);
        }).length;
      } else {
        // List rows carry only attributeCounts (no per-attribute default info)
        // - the bulk estimate stays an upper bound.
        const counts = p.marketplace?.attributeCounts;
        attrCount =
          (generateRequired ? counts?.required ?? 0 : 0) +
          (generateRecommended ? counts?.recommended ?? 0 : 0) +
          (generateOther ? counts?.other ?? 0 : 0);
      }
      tokens += baseGenerate + attrCount * perField;
      if (needsCategory && autoDetectCategory) {
        tokens += baseCategoryDetect ?? 0;
      }
    });
    return tokens * tier1Multiplier;
  }, [
    publicConfigs,
    selectedProducts,
    assignedCategory,
    autoDetectCategory,
    generatePrefilled,
    generateRequired,
    generateRecommended,
    generateOther,
  ]);

  /** `45 s` / `2 min 15 s` - locale-neutral units. */
  const formatDuration = (totalSeconds: number): string => {
    if (totalSeconds < 60) {
      return `${totalSeconds} s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const rest = totalSeconds % 60;
    return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
  };

  const anyGroupSelected = generateRequired || generateRecommended || generateOther;

  /**
   * Selected products whose marketplace category is not set yet
   * (`status === category_not_selected`) and that have not been assigned a
   * category in-modal. These are the rows shown in the category gate.
   */
  const productsNeedingCategory = useMemo(
    () =>
      selectedProducts.filter(
        (p) =>
          typeof p.wawiArtikelId === 'number' &&
          p.marketplace?.status ===
            MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED &&
          assignedCategory[p.wawiArtikelId] === undefined
      ),
    [selectedProducts, assignedCategory]
  );

  // Products skipped on submit: still without a category while auto-detect is
  // off. With auto-detect on the backend handles them, so nothing is skipped.
  const skippedCount = autoDetectCategory ? 0 : productsNeedingCategory.length;

  // Single-variant additional context: composed string + submit blockers
  // (over the 50k limit / a file still parsing). Bulk ignores the block.
  const additionalContextText =
    variant === 'single' ? buildAdditionalContext(additionalContext) : '';
  const additionalContextTooLong =
    additionalContextText.length > ADDITIONAL_CONTEXT_MAX_LENGTH;
  const additionalContextParsing =
    variant === 'single' && hasParsingAdditionalContextFiles(additionalContext);

  // Field count for the context token surcharge (single variant only): the
  // product's attributes in the SELECTED groups - same disjoint grouping as
  // `attributeCounts` (required / recommended-only / other) - excluding the
  // types the AI never generates (image / document / url / label).
  const surchargeFieldCount = (() => {
    if (variant !== 'single') {
      return 0;
    }
    let count = 0;
    (selectedProducts[0]?.marketplace?.attributes ?? []).forEach((attr) => {
      if (NON_GENERATED_ATTR_TYPES.has((attr.type ?? '').toLowerCase())) {
        return;
      }
      // With `generatePrefilled` off the backend skips Wawi-pre-filled
      // attributes - they are not part of the generation chunks either.
      if (!generatePrefilled && attr.defaultValue != null) {
        return;
      }
      const included = attr.required
        ? generateRequired
        : attr.recommended
        ? generateRecommended
        : generateOther;
      if (included) {
        count += 1;
      }
    });
    return count;
  })();
  // UI projection of the server surcharge formula; 0 for an empty context,
  // null when the rate config key is missing (surcharge row hidden).
  const additionalContextSurcharge =
    variant === 'single'
      ? estimateAdditionalContextTokens(
          publicConfigs,
          additionalContextText.trim().length,
          surchargeFieldCount
        )
      : 0;

  const canSubmit =
    !isSubmitting &&
    selectedCount > 0 &&
    Boolean(source) &&
    Boolean(language) &&
    (!listingActive || anyGroupSelected) &&
    !additionalContextTooLong &&
    !additionalContextParsing;

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    let listingItems: BulkGenerateConfirmParams['listingItems'];
    if (listingActive) {
      listingItems = [];
      selectedProducts.forEach((p) => {
        const id = p.wawiArtikelId;
        if (typeof id !== 'number') {
          return;
        }
        const needsCategory =
          p.marketplace?.status ===
            MarketplaceProductFieldsView.status.CATEGORY_NOT_SELECTED &&
          assignedCategory[id] === undefined;
        // With auto-detect on, the backend resolves the category, so keep the
        // product. With it off, products still missing a category are skipped.
        if (!needsCategory || autoDetectCategory) {
          listingItems!.push({ wawiArtikelId: id });
        }
      });
    }
    onConfirm({
      source,
      language,
      prepareListing: listingActive,
      // Marketplace is always auto-detected from the selected channel (no override).
      marketplace: null,
      generateRequired,
      generateRecommended,
      generateOther,
      generatePrefilled: listingActive ? generatePrefilled : false,
      onlyEmptyFields: listingActive ? onlyEmptyFields : false,
      listingItems,
      autoDetectCategory: listingActive ? autoDetectCategory : false,
      // The URL-path opt-in belongs to the classic CM bulk flow only.
      includeUrlPath: listingActive ? false : includeUrlPath,
      // Same rule for the empty-field policy: classic CM bulk flow only.
      emptyFieldPolicy: listingActive ? 'keepOriginal' : emptyFieldPolicy,
      // Only the single variant carries the context, and only when non-empty
      // (empty → field omitted from the request).
      additionalContext:
        variant === 'single' && additionalContextText.trim().length > 0
          ? additionalContextText
          : undefined,
    });
  };

  const refreshPending = (syncStatus?.pendingRefresh?.pending ?? 0) > 0;

  // Fixed list of sync rows so the widget never reflows: every row is always
  // rendered, showing a spinner while loading and a status dot + value after.
  // `refreshTypes` + `refreshLabel` mark rows that can be refreshed individually.
  const syncRows: Array<{
    key: string;
    label: string;
    tone: 'ok' | 'warn' | 'error' | 'muted';
    value: string;
    refreshTypes?: MarketplaceRefreshType[];
    refreshLabel?: string;
    /** Row refresh already queued - disable its button ("waiting for worker"). */
    refreshDisabled?: boolean;
  }> = (() => {
    const worker = syncStatus?.worker;
    const cats = syncStatus?.categoriesSync;
    const attrs = syncStatus?.attributesSync;
    const catAttrs = syncStatus?.categoryAttributesSync;
    const queue = syncStatus?.pendingRefresh;
    const pending = queue?.pending ?? 0;
    const failed = queue?.failed ?? 0;
    // A category_attributes refresh is already queued for the worker.
    const categoryAttrsPending =
      pending > 0 && (queue?.types ?? []).includes('category_attributes');
    const dateWithAge = (
      iso: string | null | undefined,
      ageDays: number | null | undefined,
      fresh: boolean | undefined
    ): string => {
      const date = formatSyncDate(iso);
      if (!fresh && typeof ageDays === 'number') {
        return `${date} · ${t('marketplace_listing_sync_age_days', { count: ageDays })}`;
      }
      return date;
    };
    // Human label for a still-pending `pendingRefresh.types` entry (our client
    // types: categories / channel_attributes / category_attributes).
    const refreshTypeLabel = (type: string): string =>
      type === 'categories'
        ? t('marketplace_listing_sync_row_categories')
        : type === 'channel_attributes'
        ? t('marketplace_listing_sync_row_attributes')
        : type === 'category_attributes'
        ? t('marketplace_listing_sync_type_category_attributes')
        : type;
    const pendingTypes = (queue?.types ?? []).map(refreshTypeLabel).join(', ');
    return [
      {
        key: 'worker',
        label: t('marketplace_listing_sync_row_worker'),
        tone: worker?.alive === true ? 'ok' : worker?.alive === false ? 'error' : 'muted',
        value:
          worker?.alive === true
            ? t('marketplace_listing_sync_status_online')
            : worker?.alive === false
            ? t('marketplace_listing_sync_status_offline')
            : '-',
      },
      {
        key: 'categories',
        label: t('marketplace_listing_sync_row_categories'),
        tone: cats?.fresh ? 'ok' : 'warn',
        value: dateWithAge(cats?.refreshedAt, cats?.ageDays, cats?.fresh),
        refreshTypes: ['categories'],
        refreshLabel: t('marketplace_listing_sync_row_refresh_categories'),
      },
      {
        key: 'attributes',
        label: t('marketplace_listing_sync_row_attributes'),
        tone: attrs?.fresh ? 'ok' : 'warn',
        value: dateWithAge(attrs?.refreshedAt, attrs?.ageDays, attrs?.fresh),
        refreshTypes: ['channel_attributes'],
        refreshLabel: t('marketplace_listing_sync_row_refresh_attributes'),
      },
      {
        // Per-category attribute import state (`categoryAttributesSync`): the
        // schemas Wawi pulls lazily per category. Refresh queues a
        // `category_attributes` sync; while one is already queued the button
        // is disabled and the row shows "waiting for the worker".
        key: 'categoryAttributes',
        label: t('marketplace_listing_sync_type_category_attributes'),
        tone: categoryAttrsPending ? 'warn' : catAttrs?.lastRefreshedAt ? 'ok' : 'warn',
        value: categoryAttrsPending
          ? t('marketplace_listing_sync_waiting_worker')
          : catAttrs
          ? `${formatSyncDate(catAttrs.lastRefreshedAt)} · ${t(
              'marketplace_listing_sync_category_attrs_counts',
              {
                categories: catAttrs.categoriesWithAttributes ?? 0,
                attributes: catAttrs.attributesTotal ?? 0,
              }
            )}`
          : '-',
        refreshTypes: ['category_attributes'],
        refreshLabel: t('marketplace_listing_sync_row_refresh_category_attributes'),
        refreshDisabled: categoryAttrsPending,
      },
      {
        key: 'queue',
        label: t('marketplace_listing_sync_row_queue'),
        tone: failed > 0 ? 'error' : pending > 0 ? 'warn' : 'ok',
        value:
          failed > 0
            ? t('marketplace_listing_sync_queue_failed', { count: failed })
            : pending > 0
            ? pendingTypes
              ? `${t('marketplace_listing_sync_queue_pending', { count: pending })} · ${pendingTypes}`
              : t('marketplace_listing_sync_queue_pending', { count: pending })
            : t('marketplace_listing_sync_queue_none'),
      },
    ];
  })();

  // Queues a metadata refresh: pass `types` for an individual sync, omit it to
  // queue all types at once (the "refresh all" header button). T7: when
  // `category_attributes` is requested, missing/stale schemas of ASSIGNED
  // categories are fetched from the marketplace directly within the request
  // (`directSchemas`) - already done when the response returns.
  const queueRefresh = useCallback(async (types?: MarketplaceRefreshType[]) => {
    if (!source) {
      return;
    }
    try {
      const result = await dispatch(
        refreshMarketplaceMetadata({ source, types, dataSourceId })
      ).unwrap();
      const direct = result.directSchemas;
      if ((direct?.failed ?? 0) > 0 && direct?.firstError) {
        // Not a blocker: the worker queue is set anyway - surface as warning.
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: direct.firstError,
          })
        );
      }
      dispatch(
        addNotification({
          type: 'success',
          title: t('notification_success'),
          message:
            (direct?.fetched ?? 0) > 0
              ? t('marketplace_listing_refresh_direct_done')
              : t('marketplace_listing_refresh_queued'),
        })
      );
      // Reflect the freshly-queued request (pendingRefresh.pending).
      dispatch(fetchMarketplaceSyncStatus({ source, dataSourceId }));
    } catch (err) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message:
            typeof err === 'string'
              ? err
              : t('error_unexpected', { defaultValue: 'Failed to queue metadata refresh' }),
        })
      );
    }
  }, [source, dataSourceId, dispatch, t]);

  /**
   * Refresh-button click: never blocked by sync state. When the target data is
   * still fresh, an inline popconfirm (✓/✕) anchored to the pressed button asks
   * the user to confirm re-queueing; stale data is queued immediately.
   */
  const handleRefreshClick = useCallback(
    (anchor: string, types?: MarketplaceRefreshType[]) => {
      if (!source) {
        return;
      }
      const targetFresh = types
        ? types.every((type) =>
            type === 'categories'
              ? syncStatus?.categoriesSync?.fresh === true
              : type === 'channel_attributes'
              ? syncStatus?.attributesSync?.fresh === true
              : syncStatus?.category?.fresh === true
          )
        : isMarketplaceSyncFresh(syncStatus);
      if (targetFresh) {
        // Second click on the same button toggles the popconfirm off.
        setRefreshConfirm((prev) => (prev?.anchor === anchor ? null : { anchor, types }));
        return;
      }
      setRefreshConfirm(null);
      void queueRefresh(types);
    },
    [source, syncStatus, queueRefresh]
  );

  /** Popconfirm anchored to the refresh button it was opened from (portal -
   * see MarketplaceSyncPopconfirm: above the modal, closes on scroll). */
  const renderRefreshConfirm = (anchor: string) => {
    if (refreshConfirm?.anchor !== anchor) {
      return null;
    }
    const { types } = refreshConfirm;
    return (
      <MarketplaceSyncPopconfirm onRequestClose={() => setRefreshConfirm(null)}>
        <span className="marketplace-sync-popconfirm__text">
          {t('marketplace_listing_refresh_fresh_confirm')}
        </span>
        <span className="marketplace-sync-popconfirm__actions">
          <button
            type="button"
            className="marketplace-sync-popconfirm__yes"
            aria-label={t('yes')}
            title={t('yes')}
            onClick={() => {
              setRefreshConfirm(null);
              void queueRefresh(types);
            }}
          >
            ✓
          </button>
          <button
            type="button"
            className="marketplace-sync-popconfirm__no"
            aria-label={t('no')}
            title={t('no')}
            onClick={() => setRefreshConfirm(null)}
          >
            ✕
          </button>
        </span>
      </MarketplaceSyncPopconfirm>
    );
  };

  // Manual re-check of the worker sync-status (no auto-polling): the user
  // presses "check again" to see whether a queued refresh has completed.
  const handleCheckSyncAgain = useCallback(() => {
    if (!source) {
      return;
    }
    dispatch(fetchMarketplaceSyncStatus({ source, dataSourceId }));
  }, [source, dataSourceId, dispatch]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title=""
      hideCloseButton
      isCentered
      className="content-master-bulk-modal"
    >
      <div className="content-master-bulk-modal__inner">
        <h3 className="content-master-bulk-modal__title">
          {listingActive
            ? t('marketplace_listing_modal_title')
            : t('content_master_bulk_modal_title')}
        </h3>
        <div className="content-master-bulk-modal__subtitle">
          {variant === 'single'
            ? t('marketplace_listing_single_modal_subtitle')
            : t('content_master_bulk_modal_subtitle', { count: selectedCount })}
        </div>

        <label className="content-master-bulk-modal__field">
          <span className="content-master-bulk-modal__field-label">
            {t('content_master_bulk_modal_select_language')}
          </span>
          <select
            className="content-master-bulk-modal__select"
            value={language}
            disabled={isSubmitting || languages.length === 0}
            onChange={(event) => setLanguage(event.target.value)}
          >
            {languages.map((lang, idx) => {
              const code = wawiLanguageToApiValue(lang);
              if (!code) {
                return null;
              }
              return (
                <option key={`${code}-${lang.kSprache ?? idx}`} value={code}>
                  {wawiLanguageLabel(lang, preferDeUi)}
                </option>
              );
            })}
          </select>
        </label>

        {listingActive && (
          <div className="content-master-bulk-modal__marketplace">
                <fieldset className="content-master-bulk-modal__groups">
                  <legend className="content-master-bulk-modal__field-label">
                    {t('marketplace_listing_groups_legend')}
                  </legend>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={generateRequired}
                      disabled={isSubmitting}
                      onChange={(event) => setGenerateRequired(event.target.checked)}
                    />
                    <span>{t('marketplace_listing_group_required')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={generateRecommended}
                      disabled={isSubmitting}
                      onChange={(event) => setGenerateRecommended(event.target.checked)}
                    />
                    <span>{t('marketplace_listing_group_recommended')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={generateOther}
                      disabled={isSubmitting}
                      onChange={(event) => setGenerateOther(event.target.checked)}
                    />
                    <span>{t('marketplace_listing_group_other')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={generatePrefilled}
                      disabled={isSubmitting}
                      onChange={(event) => setGeneratePrefilled(event.target.checked)}
                    />
                    <span>{t('marketplace_listing_generate_prefilled_label')}</span>
                    <InfoPopover
                      text={t('marketplace_listing_generate_prefilled_tooltip')}
                      ariaLabel={t('marketplace_listing_generate_prefilled_label')}
                    />
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={onlyEmptyFields}
                      disabled={isSubmitting}
                      onChange={(event) => setOnlyEmptyFields(event.target.checked)}
                    />
                    <span>{t('marketplace_listing_only_empty_fields_label')}</span>
                    <InfoPopover
                      text={t('marketplace_listing_only_empty_fields_tooltip')}
                      ariaLabel={t('marketplace_listing_only_empty_fields_label')}
                    />
                  </label>
                </fieldset>

                {productsNeedingCategory.length > 0 && (
                  <div className="marketplace-category-gate">
                    <div className="marketplace-category-gate__head">
                      <span className="marketplace-category-gate__title">
                        {t('marketplace_listing_category_gate_title', {
                          count: productsNeedingCategory.length,
                        })}
                      </span>
                      <label className="content-master-bulk-modal__checkbox">
                        <input
                          type="checkbox"
                          className="content-master-checkbox"
                          checked={autoDetectCategory}
                          disabled={isSubmitting}
                          onChange={(event) => {
                            if (!event.target.checked) {
                              setAutoDetectCategory(false);
                            } else if (isAutoDetectWarningDismissed()) {
                              setAutoDetectCategory(true);
                              setPickerOpenFor(null);
                            } else {
                              // Consent first: the flag is set on confirm.
                              setAutoDetectWarningOpen(true);
                            }
                          }}
                        />
                        <span>{t('marketplace_listing_auto_detect_category')}</span>
                      </label>
                    </div>
                    <div className="marketplace-category-gate__hint">
                      {t('marketplace_listing_category_gate_hint')}
                    </div>
                    <ul className="marketplace-category-gate__list">
                      {productsNeedingCategory.map((product) => {
                        const id = product.wawiArtikelId as number;
                        return (
                          <li key={id} className="marketplace-category-gate__item">
                            <div className="marketplace-category-gate__row">
                              <div className="marketplace-category-gate__product">
                                <ContentMasterProductThumb
                                  imageUrl={product.mainImageUrl}
                                  alt=""
                                />
                                <span className="marketplace-category-gate__product-title">
                                  {product.title || `#${id}`}
                                </span>
                                {product.sku ? (
                                  <span className="marketplace-category-gate__product-sku">
                                    {product.sku}
                                  </span>
                                ) : null}
                              </div>
                              <div className="marketplace-category-gate__actions">
                                <button
                                  type="button"
                                  className="content-master-link-button"
                                  disabled={autoDetectCategory || isSubmitting}
                                  onClick={() =>
                                    setPickerOpenFor((prev) => (prev === id ? null : id))
                                  }
                                >
                                  {t('marketplace_listing_assign_category')}
                                </button>
                              </div>
                            </div>
                            {pickerOpenFor === id && !autoDetectCategory && (
                              <MarketplaceCategoryPicker
                                source={source}
                                wawiArtikelId={id}
                                dataSourceId={dataSourceId}
                                onAssigned={(_kCategory, categoryName) => {
                                  setAssignedCategory((prev) => ({
                                    ...prev,
                                    [id]: categoryName,
                                  }));
                                  setPickerOpenFor(null);
                                }}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {skippedCount > 0 && (
                      <div className="marketplace-category-gate__skip">
                        {t('marketplace_listing_category_gate_skip', { count: skippedCount })}
                      </div>
                    )}
                  </div>
                )}

                {listingSecondsEstimate > 0 && (
                  <div className="content-master-bulk-modal__token-estimate">
                    <span className="content-master-bulk-modal__token-estimate-label">
                      {t('marketplace_listing_time_estimate_label')}
                    </span>
                    <span className="content-master-bulk-modal__token-estimate-value">
                      ~{formatDuration(listingSecondsEstimate)}
                    </span>
                  </div>
                )}

                {listingTokensEstimate != null && listingTokensEstimate > 0 && (
                  <div className="content-master-bulk-modal__token-estimate">
                    <span className="content-master-bulk-modal__token-estimate-label">
                      {t('marketplace_listing_token_estimate_label')}
                    </span>
                    <span className="content-master-bulk-modal__token-estimate-value">
                      {/* Base estimate + the additional-context surcharge. */}
                      ~
                      {Math.round(
                        listingTokensEstimate + (additionalContextSurcharge ?? 0)
                      ).toLocaleString()}{' '}
                      {t('content_master_bulk_modal_token_estimate_unit')}
                    </span>
                  </div>
                )}

                <div className="marketplace-sync-widget" aria-live="polite">
                  <div className="marketplace-sync-widget__head">
                    <span className="marketplace-sync-widget__heading">
                      {t('marketplace_listing_sync_widget_title')}
                    </span>
                    <span className="marketplace-sync-widget__head-actions">
                      {refreshPending ? (
                        <button
                          type="button"
                          className="marketplace-sync-widget__btn"
                          onClick={handleCheckSyncAgain}
                          disabled={syncStatusLoading}
                        >
                          {syncStatusLoading ? (
                            <span
                              className="marketplace-sync-widget__spinner marketplace-sync-widget__spinner--btn"
                              aria-hidden
                            />
                          ) : null}
                          {t('marketplace_listing_refresh_check_again')}
                        </button>
                      ) : null}
                      <span className="marketplace-sync-popconfirm-anchor">
                        <button
                          type="button"
                          className="marketplace-sync-widget__btn"
                          onClick={() => handleRefreshClick('all')}
                          disabled={refreshSubmitting}
                        >
                          {refreshSubmitting ? (
                            <span
                              className="marketplace-sync-widget__spinner marketplace-sync-widget__spinner--btn"
                              aria-hidden
                            />
                          ) : null}
                          {refreshSubmitting ? t('loading_text') : t('marketplace_listing_refresh')}
                        </button>
                        {renderRefreshConfirm('all')}
                      </span>
                    </span>
                  </div>

                  <div className="marketplace-sync-widget__rows">
                    {syncRows.map((row) => (
                      <div key={row.key} className="marketplace-sync-widget__row">
                        <span className="marketplace-sync-widget__row-label">{row.label}</span>
                        <span className="marketplace-sync-widget__row-status">
                          {syncStatusLoading ? (
                            <span
                              className="marketplace-sync-widget__spinner marketplace-sync-widget__spinner--row"
                              aria-hidden
                            />
                          ) : (
                            <>
                              {row.refreshTypes ? (
                                <span className="marketplace-sync-popconfirm-anchor">
                                  <button
                                    type="button"
                                    className="marketplace-sync-widget__btn marketplace-sync-widget__btn--row"
                                    onClick={() => handleRefreshClick(row.key, row.refreshTypes)}
                                    disabled={refreshSubmitting || row.refreshDisabled}
                                  >
                                    {row.refreshLabel}
                                  </button>
                                  {renderRefreshConfirm(row.key)}
                                </span>
                              ) : null}
                              <span
                                className={`marketplace-sync-widget__dot marketplace-sync-widget__dot--${row.tone}`}
                                aria-hidden
                              />
                              <span
                                className="marketplace-sync-widget__row-value"
                                title={row.value}
                              >
                                {row.value}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
          </div>
        )}

        {!listingActive && (
          <label className="content-master-bulk-modal__checkbox">
            <input
              type="checkbox"
              className="content-master-checkbox"
              checked={includeUrlPath}
              disabled={isSubmitting}
              onChange={(event) => setIncludeUrlPath(event.target.checked)}
            />
            <span>{t('content_master_bulk_include_url_path')}</span>
            <InfoPopover
              text={t('content_master_bulk_include_url_path_hint')}
              ariaLabel={t('content_master_bulk_include_url_path')}
            />
          </label>
        )}

        {!listingActive && (
          <EmptyFieldPolicyControl
            name="bulk-generate-empty-field-policy"
            value={emptyFieldPolicy}
            disabled={isSubmitting}
            onChange={setEmptyFieldPolicy}
          />
        )}

        {!listingActive && (
          <div className="content-master-bulk-modal__token-estimate">
            {tokenEstimate == null ? (
              <span className="content-master-bulk-modal__token-estimate-unknown">
                {t('content_master_bulk_modal_token_estimate_unknown')}
              </span>
            ) : (
              <>
                <span className="content-master-bulk-modal__token-estimate-label">
                  {t('content_master_bulk_modal_token_estimate_label')}
                </span>
                <span className="content-master-bulk-modal__token-estimate-value">
                  {Math.round(tokenEstimate).toLocaleString()}{' '}
                  {t('content_master_bulk_modal_token_estimate_unit')}
                </span>
              </>
            )}
          </div>
        )}

        {variant === 'single' ? (
          <>
            <AdditionalContextInput
              initialValue={additionalContext}
              onStateChange={setAdditionalContext}
              disabled={isSubmitting}
            />
            {additionalContextSurcharge != null && additionalContextSurcharge > 0 ? (
              <div className="additional-context__surcharge" aria-live="polite">
                {t('cm_additional_context_surcharge', {
                  tokens: additionalContextSurcharge.toLocaleString(),
                })}
              </div>
            ) : null}
          </>
        ) : null}

        <div className="content-master-bulk-modal__buttons">
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('content_master_bulk_modal_cancel')}
          </button>
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--confirm"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {listingActive
              ? t('marketplace_listing_modal_start')
              : t('content_master_bulk_modal_start')}
          </button>
        </div>
      </div>

      {/* Warning shown when the auto-detect checkbox is being turned on:
          detected categories are written straight to Wawi (live data). */}
      <AutoDetectCategoryWarningModal
        isOpen={autoDetectWarningOpen}
        onClose={() => setAutoDetectWarningOpen(false)}
        onConfirm={() => {
          setAutoDetectCategory(true);
          setPickerOpenFor(null);
          setAutoDetectWarningOpen(false);
        }}
      />
    </Modal>
  );
};

export default BulkGenerateConfirmModal;
